import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { clearAuthCookies, getAccessToken, getUserId } from '@/lib/cookies';
import { buildRedirectUrl } from '@/lib/redirect';
import { resolveProductUrl } from '@podus/utils';

const products = [
    {
        prefix: 'Podus',
        name: 'AI',
        subtitle: 'Legal Assistant',
        url: resolveProductUrl('https://podus.ai'),
        logo: '/logo/podus-ai.svg',
        description:
            'Get instant legal guidance, draft documents, and connect with verified lawyers — all powered by AI trained on Nigerian and UK law.',
    },
    {
        prefix: 'Podus',
        name: 'Contracts',
        subtitle: null,
        url: resolveProductUrl('https://contract.podus.ai'),
        logo: '/logo/podus-contract.svg',
        description:
            'Create, review, and manage legal contracts with ease. AI-powered templates and smart guidance keep your agreements airtight.',
    },
    {
        prefix: 'Podus',
        name: 'Appointment',
        subtitle: null,
        url: resolveProductUrl('https://appointment.podus.ai'),
        logo: '/logo/podus-appointment.svg',
        description:
            'Book verified legal professionals in minutes. Schedule consultations and get expert advice without the hassle of long searches.',
    },
    {
        prefix: 'Podus',
        name: 'Aid',
        subtitle: null,
        url: resolveProductUrl('https://podus.org'),
        logo: '/logo/podus-aids.svg',
        description:
            'Access legal aid and human rights support when you need it most. Connect with resources and professionals who can help.',
    },
    {
        prefix: 'Podus',
        name: 'Checker',
        subtitle: null,
        url: resolveProductUrl('https://checker.podus.org'),
        logo: '/logo/podus-cheaker.svg',
        description:
            'Run instant, comprehensive background checks on individuals and businesses. Make informed decisions with verified data.',
    },
];

export default function Products() {
    const navigate = useNavigate();
    const token = getAccessToken();

    useEffect(() => {
        if (!token) navigate('/sign-in', { replace: true });
    }, [token, navigate]);

    const handleLogout = () => {
        clearAuthCookies();
        navigate('/sign-in', { replace: true });
    };

    const handleProductClick = (productUrl: string) => {
        if (!token) return;
        window.location.href = buildRedirectUrl(productUrl, token, getUserId() ?? undefined);
    };

    if (!token) return null;

    return (
        <div className="products-bg relative flex h-dvh flex-col overflow-y-auto">
            {/* Fixed header — logo centered, logout top-right */}
            <header className="fixed inset-x-0 top-0 z-50 flex h-14 items-center justify-between px-3 bg-[#ffffff] sm:h-16 sm:px-5">
                <div className="w-16 sm:w-20" />
                <div className="flex items-center justify-center">
                    <img src="/logo/podus_full_1.svg" alt="Podus AI" className="h-6 sm:h-8" />
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="flex h-8 w-16 items-center justify-end gap-1 rounded-lg border border-[#ddd] bg-white px-2 text-[11px] font-medium text-[#333] sm:h-9 sm:w-20 sm:gap-1.5 sm:px-3 sm:text-[13px]"
                >
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                </button>
            </header>

            {/* Main content */}
            <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-20 sm:px-6 sm:pb-16">
                <h1 className="products-heading text-center text-2xl font-extrabold leading-[1.2] tracking-[-1px] text-[#C2C2C2] sm:text-[32px] sm:tracking-[-1.5px] md:text-[42px] lg:text-[52px]">
                    How will you <span className="text-[#151515]">like to proceed?</span>
                </h1>

                {/* Cards */}
                <div className="mt-6 grid w-full max-w-360 grid-cols-2 gap-2.5 sm:mt-10 sm:gap-4 md:mt-14 lg:grid-cols-5">
                    {products.map((product, i) => (
                        <div
                            key={i}
                            onClick={() => handleProductClick(product.url)}
                            className="product-card cursor-pointer rounded-xl bg-white p-3 shadow-[0_2px_16px_rgba(0,0,0,0.07)] sm:rounded-2xl sm:p-5"
                        >
                            {/* Logo area */}
                            <div className="flex h-20 w-full items-center justify-center rounded-lg bg-[#F5F5F5] sm:h-39.25 sm:rounded-xl">
                                <img
                                    src={product.logo}
                                    alt={`${product.prefix} ${product.name}`}
                                    className="h-8 w-8 object-contain sm:h-16 sm:w-16"
                                />
                            </div>

                            {/* Name */}
                            <p className="mt-2 text-xs leading-snug font-extrabold sm:mt-4 sm:text-[15px]">
                                <span className="text-[#151515]">{product.prefix} </span>
                                <span className="text-[#8D8D8D]">{product.name}</span>
                                {product.subtitle && (
                                    <span className="font-normal text-[#8D8D8D]"> {product.subtitle}</span>
                                )}
                            </p>

                            {/* Description — hidden on mobile */}
                            <p className="mt-2 hidden text-[11.5px] leading-4.25 text-[#151515] sm:block">
                                {product.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
